import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, App, Button, Card, Col, DatePicker, Descriptions, Flex, Form, Input, InputNumber, Modal, Popconfirm, Result, Row, Select, Skeleton, Space, Table, Typography } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { useState } from "react";
import { Link, useParams } from "react-router";
import { ApiError, api, unwrap, type Schemas } from "@/api/client";
import { useApplyFieldErrors, useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { useTabTitle } from "@/components/tabs/WorkTabs";
import { useI18n } from "@/i18n";
import { rupiahInput, rupiahSignedInput, taxPercent } from "../procurement/common";
import { BillStatusTag } from "./Bills";

type Bill = Schemas["Bill"];
type Payment = Schemas["SupplierPayment"];
type Line = Bill["lines"][number];
type Method = Payment["method"];
const DAY = "YYYY-MM-DD";
const TAX_CHOICES = [0, 1100, 1200];
const METHODS: Method[] = ["transfer", "cash", "giro", "other"];

/** A supplier bill: checked against the invoice while a draft, then paid. */
export function BillDetail() {
  const { billID = "" } = useParams();
  const { t } = useI18n();
  const errorText = useErrorText();
  const bill = useQuery({
    queryKey: ["finance", "bills", "detail", billID],
    queryFn: () => unwrap(api.GET("/v1/finance/bills/{billID}", { params: { path: { billID } } })),
    retry: (n, err) => !(err instanceof ApiError && err.status === 404) && n < 2,
  });
  useTabTitle(bill.data?.number);
  if (bill.isError) {
    return bill.error instanceof ApiError && bill.error.status === 404 ? <Result status="404" title={t("page.notFound.title")} /> : <Result status="error" title={errorText(bill.error)} />;
  }
  if (!bill.data) return <Skeleton active />;
  return <BillView bill={bill.data} />;
}

function useSaveBill(id: string) {
  const queryClient = useQueryClient();
  return (data: Bill) => {
    queryClient.setQueryData(["finance", "bills", "detail", id], data);
    void queryClient.invalidateQueries({ queryKey: ["finance", "bills"], predicate: (q) => q.queryKey[2] !== "detail" });
    void queryClient.invalidateQueries({ queryKey: ["finance", "payables"] });
  };
}

function BillView({ bill }: { bill: Bill }) {
  const { t, formatMoney, formatDate, formatDateTime } = useI18n();
  const { can } = useAuth();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const saved = useSaveBill(bill.id);
  const [paying, setPaying] = useState(false);
  const [voiding, setVoiding] = useState(false);
  const manage = can("finance.manage");
  const open = bill.status === "open" || bill.status === "partial";

  const voidPayment = useMutation({
    mutationFn: (paymentID: string) =>
      unwrap(api.POST("/v1/finance/bills/{billID}/payments/{paymentID}/void", { params: { path: { billID: bill.id, paymentID } } })),
    onSuccess: (b) => {
      saved(b);
      void message.success(t("bill.paymentVoided"));
    },
    onError: (err) => void message.error(errorText(err)),
  });

  const source = (
    <Space separator="·" wrap>
      {bill.purchaseOrderId &&
        (can("procurement.view") ? (
          <Link to={`/app/procurement/orders/${bill.purchaseOrderId}`}>{bill.poNumber}</Link>
        ) : (
          <span className="z-num">{bill.poNumber}</span>
        ))}
      {bill.receiptNumber && <span className="z-num">{bill.receiptNumber}</span>}
    </Space>
  );

  return (
    <>
      <Flex justify="space-between" align="center" wrap gap={12} style={{ marginBottom: 16 }}>
        <Space align="center" size={12} wrap>
          <Typography.Title level={2} style={{ margin: 0, fontSize: 28 }} className="z-num">
            {bill.number}
          </Typography.Title>
          <BillStatusTag status={bill.status} overdue={bill.overdue} />
        </Space>
        {manage && (
          <Space wrap>
            {bill.status !== "void" && bill.paid === 0 && <Button onClick={() => setVoiding(true)}>{t("bill.void")}</Button>}
            {open && (
              <Button type="primary" onClick={() => setPaying(true)}>
                {t("bill.recordPayment")}
              </Button>
            )}
          </Space>
        )}
      </Flex>
      {bill.status === "draft" && <Alert type="info" showIcon style={{ marginBottom: 16 }} title={t("bill.draftHelp")} />}
      {bill.status === "void" && <Alert type="warning" showIcon style={{ marginBottom: 16 }} title={t("bill.voided", { note: bill.voidNote })} />}
      <Row gutter={[16, 16]}>
        <Col xs={24} xl={9}>
          <Card size="small">
            <Descriptions
              column={1}
              size="small"
              items={[
                { key: "supplier", label: t("po.supplier"), children: <Link to={`/app/procurement/suppliers/${bill.supplierId}`}>{bill.supplierName}</Link> },
                { key: "source", label: t("bill.source"), children: source },
                { key: "terms", label: t("supplier.terms"), children: bill.termsDays === 0 ? t("po.cod") : t("po.paymentTermsValue", { n: bill.termsDays }) },
                { key: "method", label: t("supplier.method"), children: t(`supplier.method.${bill.supplier.paymentMethod}`) },
                {
                  key: "bank",
                  label: t("supplier.bank"),
                  children: [bill.supplier.bankName, bill.supplier.bankAccountNo, bill.supplier.bankAccountName].filter(Boolean).join(" · ") || "—",
                },
                ...(bill.status !== "draft"
                  ? [
                      { key: "invoice", label: t("bill.invoiceNo"), children: bill.invoiceNo || "—" },
                      { key: "invoiceDate", label: t("bill.invoiceDate"), children: formatDate(bill.invoiceDate) },
                      { key: "dueDate", label: t("bill.dueDate"), children: formatDate(bill.dueDate) },
                      ...(bill.confirmedAt
                        ? [{ key: "confirmed", label: t("bill.confirmedBy"), children: `${bill.confirmedName} · ${formatDateTime(bill.confirmedAt)}` }]
                        : []),
                      ...(bill.note ? [{ key: "note", label: t("supplier.note"), children: <span style={{ whiteSpace: "pre-wrap" }}>{bill.note}</span> }] : []),
                    ]
                  : []),
              ]}
            />
          </Card>
        </Col>
        <Col xs={24} xl={15}>
          {bill.status === "draft" && manage ? <DraftEditor bill={bill} /> : <Lines bill={bill} />}
        </Col>
        {bill.status !== "draft" && (
          <Col span={24}>
            <Card size="small" title={t("bill.payments")}>
              <Table<Payment>
                size="small"
                rowKey="id"
                pagination={false}
                dataSource={bill.payments}
                locale={{ emptyText: t("bill.noPayments") }}
                columns={[
                  { title: t("bill.paymentNo"), dataIndex: "number", width: 130, render: (v: string) => <span className="z-num">{v}</span> },
                  { title: t("bill.paidOn"), dataIndex: "paidOn", width: 130, render: (v: string) => formatDate(v) },
                  { title: t("supplier.method"), dataIndex: "method", width: 120, render: (v: Method) => t(`supplier.method.${v}`) },
                  { title: t("bill.paidFrom"), dataIndex: "accountName", width: 160, render: (v: string, p) => v || p.creditNumber || "—" },
                  { title: t("bill.reference"), render: (_, p) => [p.reference, p.note].filter(Boolean).join(" · ") || "—" },
                  { title: t("po.createdBy"), dataIndex: "createdName", width: 130 },
                  {
                    title: t("bill.amount"),
                    dataIndex: "amount",
                    width: 150,
                    align: "right",
                    render: (n: number, p) =>
                      p.status === "void" ? (
                        <Typography.Text delete type="secondary" className="z-num">
                          {formatMoney(n)}
                        </Typography.Text>
                      ) : (
                        <span className="z-num">{formatMoney(n)}</span>
                      ),
                  },
                  {
                    width: 110,
                    render: (_, p) =>
                      p.status === "void" ? (
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          {t("bill.paymentVoid")}
                        </Typography.Text>
                      ) : manage ? (
                        <Popconfirm title={t("bill.voidPaymentConfirm")} okText={t("bill.void")} cancelText={t("common.cancel")} onConfirm={() => voidPayment.mutate(p.id)}>
                          <Button size="small" type="link" danger>
                            {t("bill.void")}
                          </Button>
                        </Popconfirm>
                      ) : null,
                  },
                ]}
              />
              <Flex justify="flex-end" gap={32} style={{ marginTop: 12 }}>
                <Typography.Text>
                  {t("bill.paid")}: <span className="z-num">{formatMoney(bill.paid)}</span>
                </Typography.Text>
                <Typography.Text strong>
                  {t("bill.balance")}: <span className="z-num">{formatMoney(bill.balance)}</span>
                </Typography.Text>
              </Flex>
            </Card>
          </Col>
        )}
      </Row>
      <PaymentModal bill={bill} open={paying} onClose={() => setPaying(false)} />
      <VoidModal bill={bill} open={voiding} onClose={() => setVoiding(false)} />
    </>
  );
}

function Totals({ bill }: { bill: Pick<Bill, "subtotal" | "tax" | "taxRateBp" | "adjustment" | "adjustmentNote" | "total"> }) {
  const { t, formatMoney } = useI18n();
  const row = (label: string, value: number, strong?: boolean) => (
    <Flex justify="space-between" gap={24} style={{ minWidth: 300 }}>
      <Typography.Text type={strong ? undefined : "secondary"} strong={strong}>
        {label}
      </Typography.Text>
      <Typography.Text strong={strong} className="z-num">
        {formatMoney(value)}
      </Typography.Text>
    </Flex>
  );
  return (
    <Flex vertical align="flex-end" gap={4} style={{ marginTop: 12 }}>
      {row(t("po.subtotal"), bill.subtotal)}
      {row(bill.taxRateBp ? t("po.taxRate", { rate: taxPercent(bill.taxRateBp) }) : t("po.taxNone"), bill.tax)}
      {bill.adjustment !== 0 && row(bill.adjustmentNote || t("bill.adjustment"), bill.adjustment)}
      {row(t("po.total"), bill.total, true)}
    </Flex>
  );
}

function Lines({ bill }: { bill: Bill }) {
  const { t, formatMoney, formatNumber } = useI18n();
  return (
    <Card size="small" title={t("bill.lines")}>
      <Table<Line>
        size="small"
        rowKey="id"
        pagination={false}
        dataSource={bill.lines}
        columns={[
          {
            title: t("catalog.code"),
            render: (_, l) => (
              <span>
                <span className="z-num">{l.skuCode}</span>
                <br />
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {l.skuName}
                </Typography.Text>
              </span>
            ),
          },
          { title: t("po.quantity"), dataIndex: "quantity", width: 90, align: "right", render: (n: number) => <span className="z-num">{formatNumber(n)}</span> },
          { title: t("po.unitPrice"), dataIndex: "unitPrice", width: 150, align: "right", render: (n: number) => <span className="z-num">{formatMoney(n)}</span> },
          { title: t("bill.amount"), dataIndex: "amount", width: 160, align: "right", render: (n: number) => <span className="z-num">{formatMoney(n)}</span> },
        ]}
      />
      <Totals bill={bill} />
    </Card>
  );
}

type DraftForm = {
  invoiceNo?: string;
  invoiceDate: Dayjs;
  dueDate: Dayjs;
  taxRateBp: number;
  adjustment?: number | null;
  adjustmentNote?: string;
  note?: string;
  prices: Record<string, number | null>;
};

/** The draft beside the supplier's invoice: number, dates, prices, PPN, extras. */
function DraftEditor({ bill }: { bill: Bill }) {
  const { t, formatMoney, formatNumber } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const applyFieldErrors = useApplyFieldErrors();
  const saved = useSaveBill(bill.id);
  const [form] = Form.useForm<DraftForm>();
  const values = Form.useWatch([], form) as DraftForm | undefined;

  const body = (v: DraftForm) => ({
    invoiceNo: v.invoiceNo?.trim() ?? "",
    invoiceDate: v.invoiceDate.format(DAY),
    dueDate: v.dueDate.format(DAY),
    taxRateBp: v.taxRateBp,
    adjustment: v.adjustment ?? 0,
    adjustmentNote: v.adjustmentNote?.trim() ?? "",
    note: v.note?.trim() ?? "",
    lines: bill.lines.map((l) => ({ id: l.id, unitPrice: v.prices?.[l.id] ?? 0 })),
  });
  const save = useMutation({
    mutationFn: async ({ v, confirm }: { v: DraftForm; confirm: boolean }) => {
      const b = await unwrap(api.PUT("/v1/finance/bills/{billID}", { params: { path: { billID: bill.id } }, body: body(v) }));
      return confirm ? unwrap(api.POST("/v1/finance/bills/{billID}/confirm", { params: { path: { billID: bill.id } } })) : b;
    },
    onSuccess: (b, { confirm }) => {
      saved(b);
      void message.success(confirm ? t("bill.confirmed") : t("bill.saved"));
    },
    onError: (err) => {
      if (!applyFieldErrors(form, err)) void message.error(errorText(err));
    },
  });

  // Live totals, the way the server counts them.
  const subtotal = bill.lines.reduce((sum, l) => sum + l.quantity * (values?.prices?.[l.id] ?? l.unitPrice), 0);
  const taxRateBp = values?.taxRateBp ?? bill.taxRateBp;
  const tax = Math.floor((subtotal * taxRateBp + 5000) / 10000);
  const adjustment = values?.adjustment ?? 0;
  const live = { subtotal, tax, taxRateBp, adjustment, adjustmentNote: values?.adjustmentNote ?? "", total: subtotal + tax + adjustment };

  return (
    <Card size="small" title={t("bill.check")}>
      <Form<DraftForm>
        form={form}
        layout="vertical"
        requiredMark={false}
        initialValues={{
          invoiceNo: bill.invoiceNo,
          invoiceDate: dayjs(bill.invoiceDate, DAY),
          dueDate: dayjs(bill.dueDate, DAY),
          taxRateBp: bill.taxRateBp,
          adjustment: bill.adjustment || null,
          adjustmentNote: bill.adjustmentNote,
          note: bill.note,
          prices: Object.fromEntries(bill.lines.map((l) => [l.id, l.unitPrice])),
        }}
        onValuesChange={(changed: Partial<DraftForm>) => {
          // The due date follows the invoice date by the term.
          if (changed.invoiceDate) form.setFieldValue("dueDate", changed.invoiceDate.add(bill.termsDays, "day"));
        }}
      >
        <Row gutter={12}>
          <Col xs={24} sm={8}>
            <Form.Item name="invoiceNo" label={t("bill.invoiceNo")} tooltip={t("bill.invoiceNoHelp")}>
              <Input maxLength={80} className="z-num" />
            </Form.Item>
          </Col>
          <Col xs={12} sm={8}>
            <Form.Item name="invoiceDate" label={t("bill.invoiceDate")} rules={[{ required: true, message: t("field.required") }]}>
              <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} allowClear={false} />
            </Form.Item>
          </Col>
          <Col xs={12} sm={8}>
            <Form.Item name="dueDate" label={t("bill.dueDate")} rules={[{ required: true, message: t("field.required") }]}>
              <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} allowClear={false} />
            </Form.Item>
          </Col>
        </Row>
        <Table<Line>
          size="small"
          rowKey="id"
          pagination={false}
          dataSource={bill.lines}
          style={{ marginBottom: 12 }}
          columns={[
            {
              title: t("catalog.code"),
              render: (_, l) => (
                <span>
                  <span className="z-num">{l.skuCode}</span>
                  <br />
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {l.skuName}
                  </Typography.Text>
                </span>
              ),
            },
            { title: t("po.quantity"), dataIndex: "quantity", width: 80, align: "right", render: (n: number) => <span className="z-num">{formatNumber(n)}</span> },
            {
              title: t("po.unitPrice"),
              width: 180,
              render: (_, l) => (
                <Form.Item name={["prices", l.id]} noStyle>
                  <InputNumber<number> min={0} max={1_000_000_000_000} precision={0} prefix="Rp" style={{ width: "100%" }} {...rupiahInput} />
                </Form.Item>
              ),
            },
            {
              title: t("bill.amount"),
              width: 150,
              align: "right",
              render: (_, l) => <span className="z-num">{formatMoney(l.quantity * (values?.prices?.[l.id] ?? l.unitPrice))}</span>,
            },
          ]}
        />
        <Row gutter={12}>
          <Col xs={24} sm={6}>
            <Form.Item name="taxRateBp" label={t("po.tax")}>
              <Select options={TAX_CHOICES.map((bp) => ({ value: bp, label: bp ? t("po.taxRate", { rate: taxPercent(bp) }) : t("po.taxNone") }))} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item name="adjustment" label={t("bill.adjustment")} tooltip={t("bill.adjustmentHelp")}>
              <InputNumber<number> min={-1_000_000_000_000} max={1_000_000_000_000} precision={0} prefix="Rp" style={{ width: "100%" }} {...rupiahSignedInput} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={10}>
            <Form.Item name="adjustmentNote" label={t("bill.adjustmentNote")}>
              <Input maxLength={200} placeholder={t("bill.adjustmentPlaceholder")} />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item name="note" label={t("supplier.note")}>
              <Input.TextArea rows={2} maxLength={1000} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
      <Totals bill={live} />
      <Flex justify="flex-end" gap={8} style={{ marginTop: 16 }}>
        <Button loading={save.isPending && !save.variables?.confirm} onClick={() => void form.validateFields().then((v) => save.mutate({ v, confirm: false }))}>
          {t("bill.saveDraft")}
        </Button>
        <Button
          type="primary"
          loading={save.isPending && save.variables?.confirm}
          disabled={!values?.invoiceNo?.trim()}
          onClick={() => void form.validateFields().then((v) => save.mutate({ v, confirm: true }))}
        >
          {t("bill.confirm")}
        </Button>
      </Flex>
    </Card>
  );
}

function PaymentModal({ bill, open, onClose }: { bill: Bill; open: boolean; onClose: () => void }) {
  const { t, formatMoney } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const applyFieldErrors = useApplyFieldErrors();
  const saved = useSaveBill(bill.id);
  const [form] = Form.useForm<{ accountId?: string; creditId?: string; paidOn: Dayjs; amount: number; method: Method; reference?: string; note?: string }>();
  const chosen = Form.useWatch("method", form);
  const byCredit = chosen === "credit";
  // The account it is paid from becomes an entry of that account (Kas & Bank).
  const accounts = useQuery({ queryKey: ["finance", "cash", "accounts"], queryFn: () => unwrap(api.GET("/v1/finance/cash/accounts")), enabled: open });
  const openAccounts = (accounts.data?.items ?? []).filter((a) => a.active);
  const pay = useMutation({
    mutationFn: (v: { accountId?: string; creditId?: string; paidOn: Dayjs; amount: number; method: Method; reference?: string; note?: string }) =>
      unwrap(
        api.POST("/v1/finance/bills/{billID}/payments", {
          params: { path: { billID: bill.id } },
          body: {
            accountId: v.method === "credit" ? null : (v.accountId ?? null),
            creditId: v.method === "credit" ? (v.creditId ?? null) : null,
            paidOn: v.paidOn.format(DAY),
            amount: v.amount,
            method: v.method,
            reference: v.reference?.trim() ?? "",
            note: v.note?.trim() ?? "",
          },
        }),
      ),
    onSuccess: (b) => {
      saved(b);
      void message.success(t("bill.paymentSaved"));
      onClose();
    },
    onError: (err) => {
      if (!applyFieldErrors(form, err)) void message.error(errorText(err));
    },
  });
  const method = bill.supplier.paymentMethod;
  return (
    <Modal
      open={open}
      title={t("bill.recordPayment")}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={pay.isPending}
      onOk={() => form.submit()}
      onCancel={onClose}
      destroyOnHidden
    >
      <Typography.Paragraph type="secondary">{t("bill.balanceLeft", { amount: formatMoney(bill.balance) })}</Typography.Paragraph>
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        initialValues={{ paidOn: dayjs(), amount: bill.balance, method }}
        onFinish={(v) => pay.mutate(v)}
        onValuesChange={(changed: { creditId?: string }) => {
          const c = bill.credits.find((x) => x.id === changed.creditId);
          if (c) form.setFieldValue("amount", Math.min(bill.balance, c.remaining));
        }}
      >
        {byCredit ? (
          <Form.Item name="creditId" label={t("bill.creditNote")} extra={t("bill.creditNoteHelp")} rules={[{ required: true, message: t("field.required") }]}>
            <Select options={bill.credits.map((c) => ({ value: c.id, label: `${c.number} · ${formatMoney(c.remaining)}` }))} />
          </Form.Item>
        ) : (
          <Form.Item name="accountId" label={t("bill.paidFrom")} extra={openAccounts.length === 0 ? t("bill.noCashAccounts") : t("bill.paidFromHelp")}>
            <Select
              allowClear
              loading={accounts.isPending}
              placeholder={t("bill.paidFromNone")}
              options={openAccounts.map((a) => ({ value: a.id, label: `${a.name}${a.accountNo ? ` · ${a.accountNo}` : ""}` }))}
            />
          </Form.Item>
        )}
        <Row gutter={12}>
          <Col xs={24} sm={12}>
            <Form.Item name="amount" label={t("bill.amount")} rules={[{ required: true, message: t("field.required") }]}>
              <InputNumber<number> min={1} max={bill.balance} precision={0} prefix="Rp" style={{ width: "100%" }} {...rupiahInput} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="paidOn" label={t("bill.paidOn")} rules={[{ required: true, message: t("field.required") }]}>
              <DatePicker format="DD/MM/YYYY" style={{ width: "100%" }} allowClear={false} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="method" label={t("supplier.method")}>
              <Select options={[...METHODS, ...(bill.credits.length > 0 ? (["credit"] as Method[]) : [])].map((m) => ({ value: m, label: t(`supplier.method.${m}`) }))} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="reference" label={t("bill.reference")}>
              <Input maxLength={120} placeholder={t("bill.referencePlaceholder")} />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item name="note" label={t("supplier.note")}>
              <Input maxLength={500} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}

function VoidModal({ bill, open, onClose }: { bill: Bill; open: boolean; onClose: () => void }) {
  const { t } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const saved = useSaveBill(bill.id);
  const [note, setNote] = useState("");
  const run = useMutation({
    mutationFn: () => unwrap(api.POST("/v1/finance/bills/{billID}/void", { params: { path: { billID: bill.id } }, body: { note: note.trim() } })),
    onSuccess: (b) => {
      saved(b);
      onClose();
    },
    onError: (err) => void message.error(errorText(err)),
  });
  return (
    <Modal
      open={open}
      title={t("bill.voidTitle")}
      okText={t("bill.void")}
      okButtonProps={{ danger: true, disabled: !note.trim() }}
      cancelText={t("common.cancel")}
      confirmLoading={run.isPending}
      onOk={() => run.mutate()}
      onCancel={onClose}
      destroyOnHidden
    >
      <Typography.Paragraph type="secondary">{t("bill.voidHelp")}</Typography.Paragraph>
      <Input.TextArea rows={3} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} placeholder={t("bill.voidPlaceholder")} />
    </Modal>
  );
}
